import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";
import { format } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Users, 
  FileText, 
  Search, 
  Calendar, 
  Phone, 
  Mail,
  MapPin,
  Pill,
  AlertCircle,
  Video
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useAuditLog } from "@/hooks/useAuditLog";

interface PatientWithIntake {
  member_id: string;
  intake_form: {
    id: string;
    first_name: string;
    last_name: string;
    date_of_birth: string;
    phone: string;
    email: string | null;
    city: string;
    state: string;
    insurance_type: string;
    chronic_conditions: string | null;
    current_medications: string | null;
    allergies: string | null;
    patient_photo_url: string | null;
  } | null;
  next_appointment: string | null;
  appointment_count: number;
}

export function AssignedPatients() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<PatientWithIntake | null>(null);
  const { user } = useAuth();
  const { logIntakeFormView } = useAuditLog();
  const navigate = useNavigate();

  const { data: patients, isLoading } = useQuery({
    queryKey: ["assigned-patients", user?.id],
    queryFn: async () => {
      if (!user) return [];

      // Single query: get sessions with member profile joined
      const { data: sessions, error: sessionsError } = await supabase
        .from("telehealth_sessions")
        .select(`
          member_id, scheduled_date, scheduled_time,
          member_profile:profiles!telehealth_sessions_member_id_profiles_fkey(id, display_name)
        `)
        .eq("provider_id", user.id)
        .in("status", ["scheduled", "waiting", "in_progress", "completed"])
        .order("scheduled_date", { ascending: true });

      if (sessionsError) throw sessionsError;

      // Get unique member IDs with their appointment info
      const memberMap = new Map<string, { nextAppointment: string | null; count: number }>();
      const today = new Date().toISOString().split("T")[0];

      sessions?.forEach((session: any) => {
        const existing = memberMap.get(session.member_id);
        const isUpcoming = session.scheduled_date >= today;
        
        if (!existing) {
          memberMap.set(session.member_id, {
            nextAppointment: isUpcoming ? `${session.scheduled_date} ${session.scheduled_time}` : null,
            count: 1,
          });
        } else {
          existing.count++;
          if (isUpcoming && !existing.nextAppointment) {
            existing.nextAppointment = `${session.scheduled_date} ${session.scheduled_time}`;
          }
        }
      });

      const memberIds = Array.from(memberMap.keys());
      if (memberIds.length === 0) return [];

      // Fetch intake forms for these members (bounded set from sessions)
      const { data: intakeForms, error: intakeError } = await supabase
        .from("patients")
        .select("id, user_id, first_name, last_name, date_of_birth, phone, email, city, state, insurance_type, chronic_conditions, current_medications, allergies, patient_photo_url")
        .in("user_id", memberIds);

      if (intakeError) throw intakeError;

      // Combine data
      const result: PatientWithIntake[] = memberIds.map((memberId) => {
        const intake = intakeForms?.find((f) => f.user_id === memberId);
        const appointmentInfo = memberMap.get(memberId);

        return {
          member_id: memberId,
          intake_form: intake ? {
            id: intake.id,
            first_name: intake.first_name,
            last_name: intake.last_name,
            date_of_birth: intake.date_of_birth,
            phone: intake.phone,
            email: intake.email,
            city: intake.city,
            state: intake.state,
            insurance_type: intake.insurance_type,
            chronic_conditions: intake.chronic_conditions,
            current_medications: intake.current_medications,
            allergies: intake.allergies,
            patient_photo_url: intake.patient_photo_url,
          } : null,
          next_appointment: appointmentInfo?.nextAppointment || null,
          appointment_count: appointmentInfo?.count || 0,
        };
      });

      return result.filter((p) => p.intake_form);
    },
    enabled: !!user,
  });

  const handleViewIntake = (patient: PatientWithIntake) => {
    setSelectedPatient(patient);
    if (patient.intake_form) {
      logIntakeFormView(
        patient.intake_form.id,
        patient.member_id,
        `${patient.intake_form.first_name} ${patient.intake_form.last_name}`
      );
    }
  };

  const filteredPatients = patients?.filter((patient) => {
    if (!patient.intake_form) return false;
    const searchLower = searchQuery.toLowerCase();
    return (
      patient.intake_form.first_name.toLowerCase().includes(searchLower) ||
      patient.intake_form.last_name.toLowerCase().includes(searchLower) ||
      patient.intake_form.phone.includes(searchQuery)
    );
  });

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const formatInsuranceType = (type: string) => {
    return type.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Assigned Patients
              </CardTitle>
              <CardDescription>
                Quick access to your patients' records
              </CardDescription>
            </div>
            <Badge variant="secondary" className="text-sm">
              {patients?.length || 0} patients
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search patients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Patient List */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              ))}
            </div>
          ) : filteredPatients?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No assigned patients found</p>
              <p className="text-sm">Patients will appear here after booking appointments</p>
            </div>
          ) : (
            <ScrollArea className="h-[320px]">
              <div className="space-y-2">
                {filteredPatients?.map((patient) => (
                  <div
                    key={patient.member_id}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={patient.intake_form?.patient_photo_url || undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                        {patient.intake_form
                          ? getInitials(patient.intake_form.first_name, patient.intake_form.last_name)
                          : "?"}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {patient.intake_form?.first_name} {patient.intake_form?.last_name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-xs px-1.5 py-0">
                          {patient.intake_form && formatInsuranceType(patient.intake_form.insurance_type)}
                        </Badge>
                        {patient.next_appointment && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(patient.next_appointment.split(" ")[0]), "MMM d")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => navigate(ROUTES.PROVIDER_TELEHEALTH)}
                      >
                        <Video className="h-4 w-4 mr-1" />
                        Start
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewIntake(patient)}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Intake Form Dialog */}
      <Dialog open={!!selectedPatient} onOpenChange={() => setSelectedPatient(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarImage src={selectedPatient?.intake_form?.patient_photo_url || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary font-medium">
                  {selectedPatient?.intake_form
                    ? getInitials(selectedPatient.intake_form.first_name, selectedPatient.intake_form.last_name)
                    : "?"}
                </AvatarFallback>
              </Avatar>
              <div>
                <span>
                  {selectedPatient?.intake_form?.first_name} {selectedPatient?.intake_form?.last_name}
                </span>
                <DialogDescription className="text-left">
                  DOB: {selectedPatient?.intake_form?.date_of_birth && 
                    format(new Date(selectedPatient.intake_form.date_of_birth), "MMMM d, yyyy")}
                </DialogDescription>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedPatient?.intake_form && (
            <div className="space-y-6 mt-4">
              {/* Contact Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedPatient.intake_form.phone}</span>
                  </div>
                  {selectedPatient.intake_form.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedPatient.intake_form.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {selectedPatient.intake_form.city}, {selectedPatient.intake_form.state}
                    </span>
                  </div>
                </div>
              </div>

              {/* Insurance */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Insurance
                </h4>
                <Badge variant="secondary">
                  {formatInsuranceType(selectedPatient.intake_form.insurance_type)}
                </Badge>
              </div>

              {/* Medical Information */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Medical Information
                </h4>
                <div className="space-y-3">
                  {selectedPatient.intake_form.chronic_conditions && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Chronic Conditions</p>
                      <p className="text-sm">{selectedPatient.intake_form.chronic_conditions}</p>
                    </div>
                  )}
                  {selectedPatient.intake_form.current_medications && (
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                        <Pill className="h-3 w-3" /> Current Medications
                      </p>
                      <p className="text-sm">{selectedPatient.intake_form.current_medications}</p>
                    </div>
                  )}
                  {selectedPatient.intake_form.allergies && (
                    <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                      <p className="text-xs font-medium text-destructive mb-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" /> Allergies
                      </p>
                      <p className="text-sm">{selectedPatient.intake_form.allergies}</p>
                    </div>
                  )}
                  {!selectedPatient.intake_form.chronic_conditions && 
                   !selectedPatient.intake_form.current_medications && 
                   !selectedPatient.intake_form.allergies && (
                    <p className="text-sm text-muted-foreground italic">No medical information recorded</p>
                  )}
                </div>
              </div>

              {/* Appointment Summary */}
              <div className="space-y-3 pt-3 border-t">
                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Appointment History
                </h4>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">Total visits:</span>
                  <Badge variant="outline">{selectedPatient.appointment_count}</Badge>
                </div>
                {selectedPatient.next_appointment && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span>Next: {format(new Date(selectedPatient.next_appointment.split(" ")[0]), "MMMM d, yyyy")}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
